import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Lottie from 'lottie-react';
import TechPower from "../assets/lottie/tech-power.json";

interface OurStrengthsCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const OurStrengthsCard: React.FC<OurStrengthsCardProps> = ({ title, description, icon, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    indigo: 'from-indigo-500 to-indigo-600',
    teal: 'from-teal-500 to-teal-600',
  };
  
  const iconColorClasses = {
    blue: 'bg-blue-100 text-blue-500',
    purple: 'bg-purple-100 text-purple-500',
    indigo: 'bg-indigo-100 text-indigo-500',
    teal: 'bg-teal-100 text-teal-500',
  };

  return (
    <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition duration-300 hover:transform hover:-translate-y-1">
      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${colorClasses[color]}`}></div>
      <div className="p-6 bg-white">
        <div className={`overflow-hidden w-56 h-56 mb-4 w-full rounded-lg flex items-center justify-center ${iconColorClasses[color]}`}>
          {icon}
        </div>
        <h3 className="text-xl font-bold mb-3 text-gray-800">{title}</h3>
        <p className="text-gray-600 mb-6">{description}</p>
      </div>
    </div>
  );
};

interface OurStrengthsSectionProps {
  strengths: {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
  }[];
}

const OurStrengthsSection: React.FC<OurStrengthsSectionProps> = ({ strengths }) => {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
            Our Strengths
          </span>
          <h2 className="px-4 text-2xl md:text-4xl font-bold mb-4 text-gray-900">
          Why Founders Choose Us for Their 0-1 Journey
          </h2>
          <p className="md:text-lg text-gray-600">
          We've helped 50+ founders navigate the technical challenges of building their first product
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {strengths.map((service, index) => (
            <OurStrengthsCard
              key={index}
              title={service.title}
              description={service.description}
              icon={service.icon}
              color={service.color}
            />
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-slate-900 to-blue-900 rounded-xl shadow-lg overflow-hidden">
          <div className="flex flex-col md:flex-row items-center md:pr-2">
            <div className="md:w-2/3 p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Turn Your Idea Into Your First Product?</h3>
              <p className="text-justify text-indigo-100 mb-8">
              "We know the founder struggle - limited time, tight budget, endless technical decisions. That's why we've streamlined the 0-1 process. Get your MVP built right, built fast, and built to impress both users and investors."
              </p>
              <Link 
                to="/contact" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-white text-indigo-600 font-medium hover:bg-indigo-50 transition duration-300 shadow-md"
              >
                Book Strategy Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="w-full md:w-1/3 bg-black p-8 md:p-12 flex items-center justify-center rounded-sm">
              <div className="relative w-48 h-48">
                <div className="absolute inset-0 bg-white rounded-full opacity-10 animate-pulse"></div>
                <div className="absolute inset-4 bg-white rounded-full opacity-20 animate-pulse" style={{ animationDelay: '500ms' }}></div>
                <div className="absolute inset-8 bg-white rounded-full opacity-30 animate-pulse" style={{ animationDelay: '1000ms' }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="w-16 h-16 text-white"
                  >
                    <path d="M12 2a10 10 0 1 0 10 10H12V2z"></path>
                    <path d="M12 2a10 10 0 0 1 10 10H12V2z"></path>
                    <path d="M12 12l9-3"></path>
                    <path d="M12 12l-9-3"></path>
                    <path d="M12 12l3 9"></path>
                    <path d="M12 12l-3-9"></path>
                  </svg>
                  <div className='absolute'>
                    <Lottie animationData={TechPower} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurStrengthsSection;